import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { X, Loader2, ZoomIn, ZoomOut } from "lucide-react";
import { viewDocument } from "../../../api/elearning/documents/documents";
import { getDocumentAccessMe } from "../../../api/elearning/documents/documentAccess";
import { arrayBufferStartsWithPdfMagic } from "./documentViewUtils";
import { getCachedPdfArrayBuffer, putCachedPdfArrayBuffer } from "./documentPdfViewCache";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const pageWidth = () => Math.min(920, Math.max(320, (typeof window !== "undefined" ? window.innerWidth : 800) - 56));

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.15;

/**
 * Xem PDF trong app: nội dung lấy qua API có Bearer, hiển thị bằng PDF.js (blob URL).
 * Không hiển thị URL lưu trữ GCS. Lưu ý: không thể ngăn tuyệt đối người dùng kỹ thuật sao lưu nội dung.
 */
const DocumentPdfPreviewModal = ({ document: doc, onClose }) => {
  const shellRef = useRef(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pw, setPw] = useState(pageWidth);
  const [zoom, setZoom] = useState(1);
  const [fromCache, setFromCache] = useState(false);

  const teardownUrl = useCallback(() => {
    setFileUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  useEffect(() => {
    const onResize = () => setPw(pageWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useLayoutEffect(() => {
    if (!doc?.id) return undefined;

    let cancelled = false;
    setLoadErr(null);
    setNumPages(0);
    setZoom(1);
    setFromCache(false);
    teardownUrl();

    const cached = getCachedPdfArrayBuffer(doc.id);
    if (cached && arrayBufferStartsWithPdfMagic(cached)) {
      const typed = new Blob([cached], { type: "application/pdf" });
      const url = URL.createObjectURL(typed);
      setFileUrl(url);
      setLoading(false);
      setFromCache(true);
      return () => {
        cancelled = true;
        teardownUrl();
      };
    }

    setLoading(true);

    (async () => {
      try {
        let access;
        try {
          access = await getDocumentAccessMe();
        } catch {
          access = null;
        }
        if (access && !access.exempt && access.remaining <= 0) {
          throw new Error(
            "Bạn đã hết lượt tải/xem tài liệu trong tháng. Liên hệ quản trị để được cấp thêm lượt."
          );
        }

        const res = await viewDocument(doc.id);
        const raw = res.data;
        if (!(raw instanceof Blob)) {
          throw new Error("Phản hồi không hợp lệ.");
        }
        const buf = await raw.arrayBuffer();
        if (!arrayBufferStartsWithPdfMagic(buf)) {
          throw new Error("Nội dung không phải PDF hợp lệ.");
        }
        putCachedPdfArrayBuffer(doc.id, buf);
        const typed = new Blob([buf], { type: "application/pdf" });
        if (cancelled) return;
        const url = URL.createObjectURL(typed);
        setFileUrl(url);
        setFromCache(false);
      } catch (e) {
        let msg = e?.message || "Không tải được tài liệu.";
        if (e?.response?.status === 403 && e?.response?.data && !(e.response.data instanceof Blob)) {
          const d = e.response.data;
          if (d?.message) msg = d.message;
        }
        const data = e?.response?.data;
        if (data instanceof Blob) {
          try {
            const t = await data.text();
            const j = JSON.parse(t);
            if (j?.message) msg = j.message;
          } catch {
            /* ignore */
          }
        } else if (data && typeof data === "object" && data.message) {
          msg = data.message;
        }
        if (!cancelled) setLoadErr(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      teardownUrl();
    };
  }, [doc?.id, teardownUrl]);

  useEffect(() => {
    if (!doc?.id) return undefined;
    const el = shellRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      e.stopPropagation();
      setZoom((z) => {
        const dir = e.deltaY > 0 ? -1 : 1;
        const next = z + dir * ZOOM_STEP;
        return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(next * 100) / 100));
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [doc?.id]);

  const handleClose = () => {
    teardownUrl();
    onClose?.();
  };

  if (!doc) return null;

  const pageRenderWidth = Math.max(120, Math.round(pw * zoom));
  const showZoom = Boolean(fileUrl && !loading && !loadErr);

  const shell = (
    <div
      ref={shellRef}
      className="fixed inset-0 z-[420] flex flex-col bg-slate-950/90 backdrop-blur-sm"
      onContextMenu={(e) => e.preventDefault()}
      style={{ userSelect: "none" }}
    >
      <header className="flex shrink-0 flex-wrap items-center gap-2 border-b border-white/10 bg-slate-900/95 px-3 py-3 text-white sm:gap-3 sm:px-4">
        <div className="min-w-0 flex-1 basis-[min(100%,14rem)] sm:basis-auto">
          <p className="truncate text-sm font-bold sm:text-base">{doc.file_name || "Tài liệu PDF"}</p>
          <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-400">
            Xem trong ứng dụng — không hiển thị đường dẫn lưu trữ
            {fromCache ? (
              <span className="ml-1.5 text-emerald-400/90">· Đã lưu tạm (30 phút)</span>
            ) : null}
          </p>
        </div>
        {showZoom ? (
          <div
            className="flex shrink-0 flex-col gap-0.5 rounded-xl border border-white/15 bg-white/5 py-1 pl-1 pr-1"
            role="group"
            aria-label="Thu phóng"
          >
            <div className="flex items-center gap-0.5 sm:gap-1">
              <button
                type="button"
                onClick={() => setZoom((z) => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100))}
                disabled={zoom <= ZOOM_MIN + 0.001}
                className="rounded-lg p-2 text-slate-200 transition-colors hover:bg-white/15 hover:text-white disabled:pointer-events-none disabled:opacity-35"
                aria-label="Thu nhỏ"
              >
                <ZoomOut size={18} strokeWidth={2.25} />
              </button>
              <span className="min-w-[2.85rem] select-none text-center text-[11px] font-bold tabular-nums text-slate-200 sm:text-xs">
                {Math.round(zoom * 100)}%
              </span>
              <button
                type="button"
                onClick={() => setZoom((z) => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100))}
                disabled={zoom >= ZOOM_MAX - 0.001}
                className="rounded-lg p-2 text-slate-200 transition-colors hover:bg-white/15 hover:text-white disabled:pointer-events-none disabled:opacity-35"
                aria-label="Phóng to"
              >
                <ZoomIn size={18} strokeWidth={2.25} />
              </button>
              <button
                type="button"
                onClick={() => setZoom(1)}
                className="ml-0.5 rounded-lg border border-white/10 px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-300 transition-colors hover:border-white/25 hover:bg-white/10 hover:text-white sm:text-[11px]"
              >
                100%
              </button>
            </div>
            <p
              className="px-0.5 text-center text-[9px] font-medium uppercase tracking-wide text-slate-500"
              title="Giữ Ctrl (Windows) hoặc ⌘ (Mac) và cuộn chuột"
            >
              Ctrl / ⌘ + cuộn chuột
            </p>
          </div>
        ) : null}
        <button
          type="button"
          onClick={handleClose}
          className="ml-auto shrink-0 rounded-xl p-2 text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
          aria-label="Đóng"
        >
          <X size={22} />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto px-3 py-4 sm:px-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-24 text-slate-300">
            <Loader2 className="h-10 w-10 animate-spin" aria-hidden />
            <p className="text-sm font-medium">Đang tải tài liệu…</p>
          </div>
        ) : loadErr ? (
          <div className="mx-auto max-w-md rounded-2xl border border-red-500/30 bg-red-950/40 p-6 text-center text-red-100">
            <p className="text-sm font-semibold">Không mở được PDF</p>
            <p className="mt-2 text-xs text-red-200/90">{loadErr}</p>
            <button
              type="button"
              onClick={handleClose}
              className="mt-4 rounded-xl bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-white/20"
            >
              Đóng
            </button>
          </div>
        ) : fileUrl ? (
          <div className="mx-auto flex min-w-min flex-col items-center gap-4 pb-8">
            <Document
              file={fileUrl}
              loading={
                <div className="flex items-center gap-2 py-12 text-slate-300">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              }
              error={
                <p className="py-12 text-center text-sm text-red-300">Không đọc được file PDF.</p>
              }
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={i + 1}
                  pageNumber={i + 1}
                  width={pageRenderWidth}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="mb-4 shadow-lg"
                />
              ))}
            </Document>
          </div>
        ) : null}
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(shell, document.body) : null;
};

export default DocumentPdfPreviewModal;
